import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Menu } from './menu';

export interface ToolbarMenuProps {
  icon: ReactNode;
  label: string;
  /** Number of active choices; shown as a small counter on the trigger. */
  badge?: number;
  /** Hide the label below `sm`, leaving just the icon. */
  compact?: boolean;
  children: ReactNode;
}

/**
 * A toolbar control that keeps its options in a dropdown instead of spending a
 * row on them: one button at any width, with the active count in plain sight.
 */
export const ToolbarMenu = ({ icon, label, badge, compact = false, children }: ToolbarMenuProps) => (
  <Menu
    trigger={
      <button
        type="button"
        aria-label={label}
        className={cn(
          'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-border bg-surface px-2.5',
          'text-[13px] text-ink transition-colors hover:bg-sunken',
          badge ? 'border-accent/40 text-accent-fg' : undefined,
        )}
      >
        <span className="text-ink-subtle" aria-hidden="true">
          {icon}
        </span>
        <span className={cn(compact ? 'hidden sm:inline' : undefined)}>{label}</span>
        {badge ? (
          <span className="grid size-4 place-items-center rounded-full bg-accent text-[10px] font-semibold text-white">
            {badge}
          </span>
        ) : null}
        <ChevronDown className="size-3 text-ink-subtle" aria-hidden="true" />
      </button>
    }
  >
    {children}
  </Menu>
);
